/**
 * skeletons.tsx — Panel-specific and generic loading skeletons.
 *
 * planTEK External Portal — polished loading states.
 * All skeletons use animate-pulse with animationDelay staggering.
 * All skeletons carry aria-busy="true" for accessibility.
 */

// ---------------------------------------------------------------------------
// Shared pulse bar helper
// ---------------------------------------------------------------------------
function PulseBar({
  width,
  height = 'h-3',
  delay = 0,
}: {
  width: string;
  height?: string;
  delay?: number;
}) {
  return (
    <div
      className={`${height} rounded animate-pulse`}
      style={{
        width,
        backgroundColor: 'var(--muted, #e5e7eb)',
        animationDelay: `${delay}ms`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// ProjectSummarySkeleton
// ---------------------------------------------------------------------------
export function ProjectSummarySkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading project summary" className="space-y-4">
      {/* Status badge placeholder */}
      <PulseBar width="80px" height="h-5" delay={0} />

      {/* Progress bar placeholder */}
      <PulseBar width="100%" height="h-2" delay={60} />

      {/* 3 info rows: label + value pairs */}
      {[
        { labelWidth: '60px', valueWidth: '40%' },
        { labelWidth: '80px', valueWidth: '55%' },
        { labelWidth: '50px', valueWidth: '35%' },
      ].map((row, i) => (
        <div key={i} className="flex items-center gap-3">
          <PulseBar width={row.labelWidth} delay={120 + i * 60} />
          <PulseBar width={row.valueWidth} delay={120 + i * 60 + 30} />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeliverableListSkeleton
// ---------------------------------------------------------------------------
function DeliverableCardSkeleton({ delay }: { delay: number }) {
  return (
    <div
      className="rounded-lg border p-3 space-y-2"
      style={{ borderColor: 'var(--border, #e5e7eb)' }}
    >
      <div className="flex items-center justify-between gap-2">
        {/* Title bar — 70% wide */}
        <PulseBar width="70%" height="h-3" delay={delay} />
        {/* Status badge — 40px */}
        <PulseBar width="40px" height="h-4" delay={delay + 30} />
      </div>
      {/* Date — 90px */}
      <PulseBar width="90px" height="h-3" delay={delay + 60} />
    </div>
  );
}

export function DeliverableListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading deliverables" className="space-y-2">
      <DeliverableCardSkeleton delay={0} />
      <DeliverableCardSkeleton delay={80} />
      <DeliverableCardSkeleton delay={160} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// InvoiceListSkeleton
// ---------------------------------------------------------------------------
function InvoiceCardSkeleton({ delay }: { delay: number }) {
  return (
    <div
      className="rounded-lg border p-3"
      style={{ borderColor: 'var(--border, #e5e7eb)' }}
    >
      <div className="flex items-center justify-between gap-2">
        {/* Invoice number — 60px */}
        <PulseBar width="60px" height="h-3" delay={delay} />
        {/* Amount — 80px */}
        <PulseBar width="80px" height="h-3" delay={delay + 30} />
        {/* Status badge — 50px */}
        <PulseBar width="50px" height="h-4" delay={delay + 60} />
      </div>
    </div>
  );
}

export function InvoiceListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading invoices" className="space-y-2">
      <InvoiceCardSkeleton delay={0} />
      <InvoiceCardSkeleton delay={80} />
      <InvoiceCardSkeleton delay={160} />

      {/* Footer totals placeholder */}
      <div
        className="flex justify-between pt-2 border-t"
        style={{ borderColor: 'var(--border, #e5e7eb)' }}
      >
        <PulseBar width="80px" height="h-3" delay={240} />
        <PulseBar width="90px" height="h-3" delay={270} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PanelSkeleton — generic configurable skeleton
// ---------------------------------------------------------------------------
export interface PanelSkeletonProps {
  rows?: number;
  showTitle?: boolean;
  'aria-label'?: string;
}

export function PanelSkeleton({
  rows = 3,
  showTitle = true,
  'aria-label': ariaLabel,
}: PanelSkeletonProps) {
  return (
    <div aria-busy="true" aria-label={ariaLabel} className="space-y-3">
      {showTitle && (
        <PulseBar width={`${120 + 40}px`} height="h-4" delay={0} />
      )}
      {Array.from({ length: rows }, (_, i) => (
        <PulseBar
          key={i}
          width={`${50 + (i * 23) % 40}%`}
          height="h-3"
          delay={(i + 1) * 80}
        />
      ))}
    </div>
  );
}
